import { AxiosResponse } from 'axios';
import React, { FC, useState } from 'react';
import { ModalContent, ModalFooter } from '..';
import { Cue } from '../../helpers/events/types';
import { trimWebEvent, TrimWebEventPayload } from './api';
import { customProperties as vars } from '@resi-media/resi-ui';
import PropTypes from 'prop-types';
import { TrimWebEventState } from '../TrimWebEventModal';
import { trackMixpanelEvent, MPEventName, MPEventProperty } from '../../mixpanel'

interface TrimWebEventFormProps {
  cues?: Cue[];
  handleCloseModal: () => void;
  webEventProfileId?: string;
  customerId?: string;
  webEventId?: string;
  reducer: { state: TrimWebEventState; dispatch: (reducer: { field: string; value: any }) => void };
}

enum TrimWebEventFormErrors {
  NAME = 'name',
  START_END_CUES = 'start-end-cues',
  STATUS_409 = 'status-409',
}

const TrimWebEventForm: FC<TrimWebEventFormProps> = ({
  cues,
  handleCloseModal,
  webEventProfileId,
  customerId,
  webEventId,
  reducer,
}): JSX.Element => {
  const [formErrors, setFormErrors] = useState<TrimWebEventFormErrors[]>([]);
  const [isButtonDisabled, setDisabled] = useState<boolean>(false);
  const { state, dispatch } = reducer;

  const handleCreateEvent = async (): Promise<void> => {
    setDisabled(true);
    const errors = [];
    const { webEventName, startPosition, endPosition, published } = state;

    if (!webEventName) {
      errors.push(TrimWebEventFormErrors.NAME);
    }

    if (startPosition && endPosition) {
      if (startPosition.position_in_seconds >= endPosition.position_in_seconds) {
        errors.push(TrimWebEventFormErrors.START_END_CUES);
      }
    }

    if (errors.length > 0) {
      setFormErrors(errors);
      setDisabled(false);
      return;
    }

    const trimWebEventPayload: TrimWebEventPayload = {
      webEventName,
      published,
      webEventProfileId,
    };

    if (startPosition) {
      trimWebEventPayload.startPosition = startPosition.position;
    }

    if (endPosition) {
      trimWebEventPayload.endPosition = endPosition.position;
    }

    try {
      if (webEventId && customerId) {
        const response: AxiosResponse = await trimWebEvent(trimWebEventPayload, customerId, webEventId);
        if (response.status === 200 || response.status === 201) {
          dispatch({ field: 'newWebEventLocation', value: response.headers.location });
        }
        trackMixpanelEvent(MPEventName.WEB_EVENT_TRIM, {
          [MPEventProperty.CUE_START]:  trimWebEventPayload.startPosition || 'Beginning of Event',
          [MPEventProperty.CUE_STOP]: trimWebEventPayload.endPosition || 'End of Event',
          [MPEventProperty.PLAYOUT_BEHAVIOR]: trimWebEventPayload.published ? 'ASAP Active Immediately' : 'ASAP Next Sim-live',
          [MPEventProperty.TRANSCODED_EVENT_UUID]: webEventId
        });
      }
    } catch (error) {
      if (error.response.status === 409) {
        setFormErrors([TrimWebEventFormErrors.STATUS_409]);
        setDisabled(false);
      }
      console.error(error);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch({ field: event.currentTarget.name, value: event.currentTarget.value });
  };

  const handleCueChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const cueByUuid: Cue | undefined = cues?.find((cue) => cue.uuid === event.currentTarget.value);
    dispatch({ field: event.currentTarget.name, value: cueByUuid });
  };

  return (
    <div data-testid="trim-web-event-form">
      <ModalContent>
        <div className="form-horizontal" id="webEventDownloadForm" style={{ width: '75%', margin: 'auto' }}>
          <div className="form-group">
            <label htmlFor="webEventName" className="col-sm-3 control-label">
              Name
            </label>
            <div className="col-sm-9">
              <input
                data-testid="name-input"
                className="form-control"
                name="webEventName"
                value={state.webEventName}
                onChange={handleChange}
              />
            </div>
            {formErrors.includes(TrimWebEventFormErrors.NAME) && (
              <>
                <div className="col-sm-3"></div>
                <div className="col-sm-9">
                  <p style={{ color: vars.colorError, margin: '0' }}>Please provide a name for the trimmed web event</p>
                </div>
              </>
            )}
          </div>
          <div className={`form-group`}>
            <label htmlFor="startCue" className="col-sm-3 control-label">
              Start Cue
            </label>
            <div className="col-sm-9">
              <select
                className="form-control"
                id="startPosition"
                name="startPosition"
                onBlur={handleCueChange}
                style={{ width: '100%' }}
                defaultValue=""
                data-testid="start-position-select"
              >
                <option value="">
                  Beginning of Event
                </option>
                {cues?.map((cue) => {
                  return (
                    <option key={cue.uuid} value={cue.uuid} data-testid={cue.uuid}>
                      {`Cue: ${cue.position} - ${cue.name}`}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="startPosition" className="col-sm-3 control-label">
              Stop Cue
            </label>
            <div className="col-sm-9">
              <select
                className="form-control"
                id="endPosition"
                name="endPosition"
                onBlur={handleCueChange}
                style={{ width: '100%' }}
                defaultValue=""
                data-testid="end-position-select"
              >
                <option value="">
                  End of Event
                </option>
                {cues?.map((cue) => {
                  return (
                    <option key={cue.uuid} value={cue.uuid} data-testid="select-option">
                      {`Cue: ${cue.position} - ${cue.name}`}
                    </option>
                  );
                })}
              </select>
            </div>
            {formErrors.includes(TrimWebEventFormErrors.START_END_CUES) && (
              <>
                <div className="col-sm-3"></div>
                <div className="col-sm-9">
                  <p style={{ color: vars.colorError, margin: '0' }}> The start cue must occur before the end cue</p>
                </div>
              </>
            )}
          </div>
          <div className="form-group">
            <div className="col-sm-3"></div>
            <div className="col-sm-9" style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="published"
                className="form-check-input"
                style={{ margin: '0', marginRight: vars.spacingS }}
                checked={state.published}
                onChange={(): void => dispatch({ field: 'published', value: !state.published })}
                data-testid="published-checkbox"
              />
              <p style={{ margin: '0' }}>Set trimmed Web Event as Active</p>
            </div>
            {formErrors.includes(TrimWebEventFormErrors.STATUS_409) && (
              <div className="form-group">
                <div className="col-sm-3"></div>
                <div className="col-sm-9">
                  <p style={{ color: vars.colorError, margin: '0' }}>
                    Only one web event may run concurrently on each profile.
                  </p>
                </div>
              </div>
            )}
          </div>
          {(state.startPosition || state.endPosition) && (
            <div className="form-group">
              <div className="col-sm-3 control-label"></div>
              <div className="col-sm-9">
                <div
                  className="well well-sm"
                  style={{ marginTop: 5, marginBottom: 0 }}
                  data-testid="startPositionCueNote"
                >
                  <strong>NOTE:</strong>
                  {` The actual start or stop time may be up to 6 seconds outside the selected position.`}
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <button data-testid="cancel-btn" className="btn btn-default" onClick={handleCloseModal}>
          Cancel
        </button>
        <button
          disabled={isButtonDisabled}
          data-testid="create-event-btn"
          className="btn btn-primary"
          onClick={handleCreateEvent}
        >
          Create Event
        </button>
      </ModalFooter>
    </div>
  );
};

TrimWebEventForm.displayName = 'TrimWebEventForm';

TrimWebEventForm.propTypes = {
  cues: PropTypes.array,
  handleCloseModal: PropTypes.func.isRequired,
  webEventProfileId: PropTypes.string,
  customerId: PropTypes.string,
  webEventId: PropTypes.string,
  reducer: PropTypes.any.isRequired,
};

export default TrimWebEventForm;
